import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Flame, Music, ThumbsUp, Clock, Loader2, Send, Play, Download, Trophy, Users, ChevronRight } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useAuth } from "../App";
import { toast } from "sonner";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChallengesPage() {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEntry, setShowEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({ title: "", audio_url: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const headers = (() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  })();

  useEffect(() => {
    axios.get(`${API}/api/challenges/`)
      .then(res => setChallenges(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadChallenge = async (c) => {
    try {
      const res = await axios.get(`${API}/api/challenges/${c.challenge_id}`);
      setSelected(res.data);
    } catch { setSelected(c); }
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to enter"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/challenges/${selected.challenge_id}/enter`, entryForm, { headers });
      toast.success("Entry submitted!");
      setShowEntry(false);
      setEntryForm({ title: "", audio_url: "", description: "" });
      loadChallenge(selected);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (entryId) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    try {
      const res = await axios.post(`${API}/api/challenges/entries/${entryId}/vote`, {}, { headers });
      toast.success(res.data.voted ? "Voted!" : "Vote removed");
      if (selected) loadChallenge(selected);
    } catch { toast.error("Failed to vote"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></div>;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="challenges-page">
      <SEO title="Collab Challenges" description="Weekly remix challenges. Flip the sample, win prizes." />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-orange-500/20 text-orange-400 border-0 mb-3">
            <Flame className="w-3 h-3 mr-1" /> Weekly Challenges
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-3">
            Collab <span className="text-neon-cyan">Challenges</span>
          </h1>
          <p className="text-zinc-500 max-w-lg mx-auto text-sm">
            Flip the sample pack into your own unique beat. Community votes decide the winner.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Challenge List */}
          <div className="space-y-3">
            <h2 className="text-xs text-zinc-500 uppercase tracking-wider mb-2 px-1">Active Challenges</h2>
            {challenges.length === 0 ? (
              <div className="glass border border-white/10 rounded-xl p-8 text-center">
                <Flame className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No active challenges yet</p>
                <p className="text-zinc-600 text-xs mt-1">Check back soon!</p>
              </div>
            ) : challenges.map(c => (
              <button
                key={c.challenge_id}
                onClick={() => loadChallenge(c)}
                className={`w-full text-left glass border rounded-xl p-4 transition hover:bg-white/5 ${
                  selected?.challenge_id === c.challenge_id ? "border-orange-500/50 bg-orange-500/5" : "border-white/10"
                }`}
                data-testid={`challenge-card-${c.challenge_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="font-medium text-sm text-white">{c.title}</p>
                  <Badge className={`text-[10px] ${c.status === "open" ? "bg-neon-green/20 text-neon-green" : c.status === "complete" ? "bg-orange-500/20 text-orange-400" : "bg-zinc-700 text-zinc-400"}`}>
                    {c.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.entry_count} entries</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(c.deadline).toLocaleDateString()}</span>
                </div>
                {c.winner_name && (
                  <div className="mt-2 flex items-center gap-1 text-[11px] text-orange-400">
                    <Trophy className="w-3 h-3" /> Winner: {c.winner_name}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Challenge Detail */}
          <div className="lg:col-span-2">
            {selected ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="glass border border-white/10 rounded-xl overflow-hidden"
                data-testid="challenge-detail"
              >
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="font-heading text-xl uppercase">{selected.title}</h2>
                      <p className="text-xs text-zinc-500 mt-1">Deadline: {new Date(selected.deadline).toLocaleString()}</p>
                    </div>
                    <Badge className={`${selected.status === "open" ? "bg-neon-green/20 text-neon-green" : "bg-zinc-700 text-zinc-400"}`}>
                      {selected.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-zinc-300 mb-4">{selected.description}</p>

                  {/* Sample Pack */}
                  {selected.sample_pack_url && (
                    <a href={selected.sample_pack_url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-3 p-3 rounded-lg bg-neon-cyan/5 border border-neon-cyan/20 hover:bg-neon-cyan/10 transition mb-4"
                      data-testid="sample-pack-link"
                    >
                      <Download className="w-5 h-5 text-neon-cyan" />
                      <div>
                        <p className="text-sm font-medium text-neon-cyan">{selected.sample_pack_name || "Download Sample Pack"}</p>
                        <p className="text-[11px] text-zinc-500">Download and flip it your way</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neon-cyan ml-auto" />
                    </a>
                  )}

                  {/* Rules & Prize */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-zinc-800/40">
                      <p className="text-[10px] text-zinc-500 uppercase mb-1">Rules</p>
                      <p className="text-xs text-zinc-300">{selected.rules}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-orange-500/5 border border-orange-500/20">
                      <p className="text-[10px] text-orange-400 uppercase mb-1">Prize</p>
                      <p className="text-xs text-zinc-200">{selected.prize}</p>
                    </div>
                  </div>
                </div>

                {/* Actions + Entries */}
                <div className="p-6">
                  {selected.status === "open" && user && (
                    <Dialog open={showEntry} onOpenChange={setShowEntry}>
                      <DialogTrigger asChild>
                        <Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold mb-6 w-full sm:w-auto" data-testid="enter-challenge-btn">
                          <Music className="w-4 h-4 mr-2" /> Submit Your Remix
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
                        <DialogHeader><DialogTitle className="font-heading">Submit Your Entry</DialogTitle></DialogHeader>
                        <form onSubmit={handleSubmitEntry} className="space-y-4">
                          <div>
                            <Label>Track Title</Label>
                            <Input value={entryForm.title} onChange={e => setEntryForm({ ...entryForm, title: e.target.value })}
                              className="bg-black/50 border-white/10" required data-testid="entry-title-input" />
                          </div>
                          <div>
                            <Label>Audio URL</Label>
                            <Input value={entryForm.audio_url} onChange={e => setEntryForm({ ...entryForm, audio_url: e.target.value })}
                              className="bg-black/50 border-white/10" required placeholder="https://soundcloud.com/..." data-testid="entry-audio-input" />
                          </div>
                          <div>
                            <Label>Description (optional)</Label>
                            <Textarea value={entryForm.description} onChange={e => setEntryForm({ ...entryForm, description: e.target.value })}
                              className="bg-black/50 border-white/10" rows={3} data-testid="entry-desc-input" />
                          </div>
                          <Button type="submit" disabled={submitting} className="w-full bg-orange-500 text-black font-bold" data-testid="submit-entry-btn">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                            Submit Entry
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}

                  <h3 className="text-xs uppercase text-zinc-500 tracking-wider mb-3">
                    Entries ({selected.entries?.length || 0})
                  </h3>

                  {(!selected.entries || selected.entries.length === 0) ? (
                    <p className="text-zinc-600 text-sm text-center py-8">No entries yet. Be the first to submit!</p>
                  ) : (
                    <div className="space-y-2">
                      {selected.entries.map((entry, idx) => (
                        <motion.div key={entry.entry_id}
                          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                          className={`flex items-center gap-4 p-3 rounded-lg transition ${
                            idx === 0 && selected.status === "complete" ? "bg-orange-500/10 border border-orange-500/30" : "bg-zinc-800/40 hover:bg-zinc-800/60"
                          }`}
                          data-testid={`challenge-entry-${entry.entry_id}`}
                        >
                          <span className={`font-heading text-lg w-8 text-center ${
                            idx === 0 ? "text-orange-400" : idx === 1 ? "text-zinc-300" : idx === 2 ? "text-amber-700" : "text-zinc-600"
                          }`}>
                            {idx === 0 && selected.status === "complete" ? <Trophy className="w-5 h-5 mx-auto text-orange-400" /> : `#${idx + 1}`}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{entry.title}</p>
                            <p className="text-[11px] text-zinc-500">by {entry.user_name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {entry.audio_url && (
                              <a href={entry.audio_url} target="_blank" rel="noreferrer">
                                <Button variant="ghost" size="icon" className="w-8 h-8 text-neon-cyan hover:bg-neon-cyan/10">
                                  <Play className="w-4 h-4" />
                                </Button>
                              </a>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleVote(entry.entry_id)}
                              className="text-zinc-400 hover:text-orange-400 gap-1"
                              data-testid={`vote-entry-${entry.entry_id}`}
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                              <span className="font-mono text-xs">{entry.votes}</span>
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <div className="glass border border-white/10 rounded-xl p-16 text-center">
                <Flame className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">Select a challenge to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
